/// <reference types="vite/client" />

import {
  type ClientOptions,
  CombinedError,
  fetchExchange,
  mapExchange,
  type MapExchangeOpts,
  subscriptionExchange
} from '@urql/vue';
import { retryExchange } from '@urql/exchange-retry';
import { requestPolicyExchange } from '@urql/exchange-request-policy';
import { cacheExchange } from '@urql/exchange-graphcache';
import { devtoolsExchange } from '@urql/devtools';
import { createClient as createWSClient } from 'graphql-ws';
import { useVersion } from '@stl/stores/version';
import type { CacheExchangeOpts } from '@urql/exchange-graphcache';
import _ from 'lodash';

export interface ExchangeOptions {
  mapExchange?: MapExchangeOpts;
}

// Lazily-connecting (graphql-ws's own default: no socket opens until the
// first subscription actually starts) WS client for GraphQL subscriptions.
// Same host + same relative path as VITE_GRAPHQL_ENDPOINT, protocol
// upgraded http(s) -> ws(s) -- the backend serves queries/mutations and
// subscriptions off the one endpoint, so this assumes no separate
// subscriptions route exists. Auth rides the same session cookie the
// fetch-based exchanges use below: same-origin WS handshakes send it
// automatically, no connectionParams needed.
function createSubscriptionExchange() {
  const endpoint = import.meta.env.VITE_GRAPHQL_ENDPOINT as string;
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = new URL(endpoint, `${wsProtocol}//${window.location.host}`).toString();

  const wsClient = createWSClient({ url: wsUrl });

  return subscriptionExchange({
    forwardSubscription(request) {
      const input = { ...request, query: request.query || '' };
      return {
        subscribe(sink) {
          const unsubscribe = wsClient.subscribe(input, sink);
          return { unsubscribe };
        }
      };
    }
  });
}

export function WithDefaults(
  cacheOptions: CacheExchangeOpts,
  //@ts-ignore
  options: Partial<ClientOptions> & ExchangeOptions = {}
): ClientOptions {
  const defaultExchanges = [
    devtoolsExchange,
    requestPolicyExchange({
      ttl: 5 * 60 * 1000 // 5 minutes
    }),
    cacheExchange(cacheOptions),
    mapExchange(
      options.mapExchange || {
        onError: (error) => {
          console.error(error);
          console.log(error.response);
          if (error.response?.status === 401) {
            window.location.replace('/api/auth/login');
          }
        }
      }
    ),
    retryExchange({
      retryIf: (err: CombinedError): boolean => (err && err.networkError && err.response?.status !== 403 ? true : false)
    }),
    createSubscriptionExchange(),
    fetchExchange
  ];

  // Handle exchanges specially - insert custom exchanges before fetchExchange
  let exchanges = defaultExchanges;
  if (options.exchanges && options.exchanges.length > 0) {
    // Insert custom exchanges before fetchExchange (last item)
    exchanges = [...defaultExchanges.slice(0, -1), ...options.exchanges, fetchExchange];
  }

  const mergedOptions = _.defaultsDeep({}, options, {
    url: import.meta.env.VITE_GRAPHQL_ENDPOINT as string,
    fetch: (input: RequestInfo | URL, init?: RequestInit) =>
      fetch(input, init).then((response) => {
        const { setAPIVersion } = useVersion();
        const apiVersion = response.headers.get('API-Version') || response.headers.get('X-API-Version');
        if (apiVersion) {
          setAPIVersion(apiVersion);
        }

        return response;
      }),
    fetchOptions: {
      credentials: 'include'
    },
    exchanges: exchanges
  });

  return mergedOptions;
}
